import type { LibraryTemplate } from "../library";
import { ellipse, line, logo, photo, rect, stack, t, text } from "./shorthands";

/** Druck: Flyer, Plakate, Aushänge, Gutscheine. */
const FIRST_PRINT: LibraryTemplate[] = [
  {
    id: "flyer-a5-event",
    title: "Flyer A5: Einladung",
    description: "Foto oben, Farbfläche mit Einladung und Eckdaten",
    presetId: "a5",
    topic: "event",
    style: "warm",
    pages: [
      {
        background: "background",
        nodes: [
          photo({ x: -30, y: -30, w: 1140, h: 820 }),
          rect({ x: -30, y: 790, w: 1140, h: 780, fill: "primary", name: "Farbfläche" }),
          ellipse({
            x: 770,
            y: 650,
            w: 250,
            h: 250,
            fill: "accent",
            shadow: { y: 12, blur: 36, color: "#00000033" },
            name: "Datum",
          }),
          text({
            text: "SA\n24.10.",
            style: "heading-2",
            size: 50,
            color: "on:accent",
            x: 770,
            y: 650,
            w: 250,
            h: 250,
            align: "center",
          }),
          stack({
            x: 90,
            y: 880,
            w: 840,
            gap: [14, 28],
            items: [
              t("HERZLICHE EINLADUNG", "subline", 34, "on:primary/80"),
              t("Sommerfest im Hofgarten", "heading-1", 100, "on:primary"),
              t(
                "Live-Musik, Grillbuffet und Spiele für Kinder – wir freuen uns auf euch!",
                "body",
                38,
                "on:primary/90",
              ),
            ],
          }),
          line(90, 1320, 900, "on:primary/40", 2),
          text({
            text: "Samstag, 24. Oktober · ab 15 Uhr\nHofgarten 12 · 50667 Köln",
            style: "subline",
            size: 30,
            color: "on:primary",
            x: 90,
            y: 1360,
            w: 620,
          }),
          logo({ x: 740, y: 1370, w: 250, h: 90, on: "primary", align: "right" }),
        ],
      },
    ],
  },
  {
    id: "flyer-a5-leistungen",
    title: "Flyer A5: Leistungen",
    description: "Überschrift auf Farbfläche, vier Leistungen, Kontakt unten",
    presetId: "a5",
    topic: "produkt",
    style: "natur",
    pages: [
      {
        background: "background",
        nodes: [
          rect({ x: -30, y: -30, w: 1140, h: 660, fill: "primary", name: "Farbfläche" }),
          ellipse({ x: 780, y: 360, w: 420, h: 420, fill: "accent/85", name: "Kreis" }),
          logo({ x: 90, y: 90, w: 300, h: 90, on: "primary" }),
          stack({
            x: 90,
            y: 240,
            w: 680,
            gap: 16,
            items: [
              t("CATERING & EVENTS", "subline", 30, "on:primary/80"),
              t("Frische Küche für jeden Anlass", "heading-1", 84, "on:primary"),
            ],
          }),
          ...(
            [
              [
                "Buffets für 20 bis 300 Gäste",
                "Saisonal, regional und auf Wunsch komplett vegetarisch.",
              ],
              [
                "Fingerfood & Flying Dinner",
                "Kleine Köstlichkeiten für Empfänge und Firmenfeiern.",
              ],
              ["Service-Team", "Aufbau, Ausschank und Abbau – ihr genießt einfach."],
              [
                "Individuelle Menüs",
                "Wir beraten euch persönlich und stellen euer Wunschmenü zusammen.",
              ],
            ] as const
          ).flatMap(([title, body], index) => {
            const y = 710 + index * 160;
            return [
              ellipse({ x: 90, y: y + 8, w: 36, h: 36, fill: "accent", name: "Punkt" }),
              stack({
                x: 160,
                y,
                w: 830,
                gap: 6,
                items: [t(title, "subline", 38, "text"), t(body, "body", 28, "text-muted")],
              }),
            ];
          }),
          rect({ x: -30, y: 1370, w: 1140, h: 200, fill: "surface", name: "Kontaktfläche" }),
          text({
            text: "Jetzt anfragen: 0221 123 456 · hallo@beispiel.de",
            style: "subline",
            size: 32,
            color: "text",
            x: 90,
            y: 1370,
            w: 900,
            h: 164,
            align: "center",
          }),
        ],
      },
    ],
  },
  {
    id: "plakat-a3-konzert",
    title: "Plakat A3: Konzert",
    description: "Riesige Schrift, Foto und Eckdaten – fällt aus der Entfernung auf",
    presetId: "a3",
    topic: "event",
    style: "laut",
    pages: [
      {
        background: "accent",
        nodes: [
          stack({
            x: 70,
            y: 90,
            w: 940,
            gap: 20,
            items: [
              t("Konzert\nim Park", "heading-1", 320, "text", { shrink: true }),
              t("Sa 12.09. · 19 Uhr", "subline", 64, "primary"),
            ],
          }),
          photo({ x: 70, y: 900, w: 940, h: 400 }),
          text({
            text: "Stadtpark Köln · Eintritt frei\nBei Regen in der Stadthalle",
            style: "body",
            size: 34,
            color: "text",
            x: 70,
            y: 1345,
            w: 640,
          }),
          logo({ x: 760, y: 1345, w: 250, h: 90, on: "accent", align: "right" }),
        ],
      },
    ],
  },
  {
    id: "aushang-a4-oeffnungszeiten",
    title: "Aushang A4: Öffnungszeiten",
    description: "Hinweis fürs Schaufenster mit übersichtlicher Zeitentabelle",
    presetId: "a4",
    topic: "ankuendigung",
    style: "klar",
    pages: [
      {
        background: "background",
        nodes: [
          rect({ x: -30, y: -30, w: 1140, h: 500, fill: "primary", name: "Farbfläche" }),
          stack({
            x: 90,
            y: 130,
            w: 900,
            gap: 20,
            items: [
              t("INFO", "subline", 34, "on:primary/80"),
              t("Geänderte Öffnungszeiten", "heading-1", 96, "on:primary"),
            ],
          }),
          text({
            text: "vom 23. Dezember bis 2. Januar",
            style: "subline",
            size: 44,
            color: "primary",
            x: 90,
            y: 560,
            w: 900,
          }),
          text({
            text: "Montag – Freitag\nSamstag\nSonntag & Feiertage",
            style: "subline",
            size: 44,
            color: "text",
            x: 90,
            y: 680,
            w: 560,
            name: "Tage",
          }),
          text({
            text: "9 – 18 Uhr\n10 – 14 Uhr\ngeschlossen",
            style: "subline",
            size: 44,
            color: "text",
            x: 650,
            y: 680,
            w: 340,
            align: "right",
            name: "Zeiten",
          }),
          line(90, 890, 900, "text-muted/40", 2),
          text({
            text: "Ab dem 3. Januar sind wir wieder wie gewohnt für euch da. Wir wünschen frohe Feiertage und einen guten Rutsch!",
            style: "body",
            size: 40,
            color: "text",
            x: 90,
            y: 940,
            w: 900,
          }),
          logo({ x: 90, y: 1340, w: 320, h: 100, on: "background" }),
          text({
            text: "www.beispiel.de · 0221 123 456",
            style: "caption",
            size: 30,
            color: "text-muted",
            x: 450,
            y: 1372,
            w: 540,
            align: "right",
          }),
        ],
      },
    ],
  },
  {
    id: "flyer-din-lang-programm",
    title: "DIN lang: Programm",
    description: "Ablauf einer Veranstaltung mit Uhrzeiten – für Feste und Tagungen",
    presetId: "din-lang",
    topic: "event",
    style: "traditionell",
    pages: [
      {
        background: "background",
        nodes: [
          rect({ x: -30, y: -30, w: 1140, h: 760, fill: "primary", name: "Farbfläche" }),
          logo({ x: 90, y: 100, w: 360, h: 110, on: "primary" }),
          stack({
            x: 90,
            y: 330,
            w: 900,
            gap: 20,
            items: [
              t("PROGRAMM", "subline", 40, "on:primary/80"),
              t("Jahresfest 2026", "heading-1", 130, "on:primary"),
            ],
          }),
          ...(
            [
              ["14:00", "Eröffnung", "Begrüßung durch den Vorstand und Sektempfang im Garten"],
              ["15:00", "Kaffee & Kuchen", "Selbst gebacken von unseren Mitgliedern"],
              ["16:30", "Musikprogramm", "Die Stadtkapelle spielt bekannte Stücke zum Mitsingen"],
              ["18:00", "Ehrungen", "Wir danken unseren langjährigen Mitgliedern"],
              ["19:00", "Grillabend", "Gemütlicher Ausklang mit Musik bis in die Nacht"],
            ] as const
          ).flatMap(([time, title, body], index) => {
            const y = 830 + index * 245;
            return [
              text({
                text: time,
                style: "subline",
                size: 44,
                color: "primary",
                x: 90,
                y: y + 4,
                w: 220,
                name: "Uhrzeit",
              }),
              stack({
                x: 320,
                y,
                w: 670,
                gap: 8,
                items: [t(title, "heading-2", 52, "text"), t(body, "body", 32, "text-muted")],
              }),
            ];
          }),
          line(90, 2060, 900, "primary/30", 2),
          text({
            text: "Vereinsheim am Mühlbach · Mühlenweg 3 · 12345 Musterstadt",
            style: "caption",
            size: 30,
            color: "text-muted",
            x: 90,
            y: 2100,
            w: 900,
            align: "center",
          }),
        ],
      },
    ],
  },
  {
    id: "gutschein-a6",
    title: "Gutschein A6",
    description: "Edler Gutschein mit Rahmen, Betrag und Feldern für Namen",
    presetId: "a6",
    topic: "angebot",
    style: "elegant",
    pages: [
      {
        background: "primary",
        nodes: [
          rect({
            x: 50,
            y: 50,
            w: 980,
            h: 1421,
            stroke: { color: "accent", width: 4 },
            radius: "sm",
            name: "Rahmen",
          }),
          rect({
            x: 70,
            y: 70,
            w: 940,
            h: 1381,
            stroke: { color: "accent/50", width: 2 },
            radius: "sm",
            name: "Rahmen innen",
          }),
          text({
            text: "GUTSCHEIN",
            style: "subline",
            size: 48,
            color: "accent",
            x: 90,
            y: 180,
            w: 900,
            align: "center",
          }),
          stack({
            x: 120,
            y: 470,
            w: 840,
            anchor: "center",
            align: "center",
            items: [t("für eine Stunde\nWohlfühlmassage", "heading-1", 86, "on:primary")],
          }),
          text({
            text: "50\u00a0€",
            style: "heading-1",
            size: 230,
            color: "accent",
            x: 90,
            y: 640,
            w: 900,
            align: "center",
            name: "Betrag",
          }),
          text({
            text: "Für",
            style: "caption",
            size: 30,
            color: "on:primary/70",
            x: 150,
            y: 1050,
            w: 120,
          }),
          line(270, 1082, 660, "on:primary/50", 2),
          text({
            text: "Von",
            style: "caption",
            size: 30,
            color: "on:primary/70",
            x: 150,
            y: 1160,
            w: 120,
          }),
          line(270, 1192, 660, "on:primary/50", 2),
          logo({ x: 390, y: 1270, w: 300, h: 90, on: "primary", align: "center" }),
          text({
            text: "Einlösbar bis 31.12.2027",
            style: "caption",
            size: 24,
            color: "on:primary/60",
            x: 90,
            y: 1395,
            w: 900,
            align: "center",
          }),
        ],
      },
    ],
  },
];

/** One dish: name and price in a row, the description below. */
function dish(y: number, name: string, price: string, description: string) {
  return [
    text({
      text: name,
      style: "subline",
      size: 34,
      color: "text",
      x: 90,
      y,
      w: 700,
      h: 48,
      name: "Gericht",
    }),
    text({
      text: price,
      style: "subline",
      size: 34,
      color: "primary",
      x: 800,
      y,
      w: 190,
      h: 48,
      align: "right",
      name: "Preis",
    }),
    text({
      text: description,
      style: "body",
      size: 26,
      color: "text-muted",
      x: 90,
      y: y + 50,
      w: 740,
      h: 40,
      name: "Beschreibung",
    }),
  ];
}

/** Heading of a menu section with a short line. */
function menuSection(y: number, title: string) {
  return [
    text({
      text: title,
      style: "heading-2",
      size: 44,
      color: "primary",
      x: 90,
      y,
      w: 900,
      h: 60,
    }),
    line(90, y + 68, 120, "accent", 4),
  ];
}

const MORE_PRINT: LibraryTemplate[] = [
  {
    id: "speisekarte-a4",
    title: "Speisekarte A4",
    description: "Mittagskarte mit Vorspeisen, Hauptgerichten und Dessert, Preise rechtsbündig",
    presetId: "a4",
    topic: "angebot",
    style: "traditionell",
    tags: [
      "speisekarte",
      "menü",
      "restaurant",
      "gastro",
      "karte",
      "preise",
      "café",
      "mittagstisch",
    ],
    pages: [
      {
        background: "background",
        nodes: [
          logo({ x: 340, y: 80, w: 400, h: 100, on: "background", align: "center" }),
          text({
            text: "Mittagskarte",
            style: "heading-1",
            size: 100,
            color: "text",
            x: 90,
            y: 200,
            w: 900,
            align: "center",
          }),
          text({
            text: "Montag bis Freitag · 11:30 – 14:30 Uhr",
            style: "subline",
            size: 30,
            color: "text-muted",
            x: 90,
            y: 330,
            w: 900,
            align: "center",
          }),
          line(390, 400, 300, "accent", 3),
          ...menuSection(440, "Vorspeisen"),
          ...dish(540, "Kürbissuppe", "6,50\u00a0€", "mit Ingwer, Kürbiskernöl und Brot"),
          ...dish(650, "Bunter Salat", "7,90\u00a0€", "Blattsalate, Radieschen, Hausdressing"),
          ...menuSection(770, "Hauptgerichte"),
          ...dish(870, "Gnocchi mit Salbeibutter", "13,90\u00a0€", "hausgemacht, mit Parmesan"),
          ...dish(980, "Hähnchenbrust", "15,50\u00a0€", "mit Ofengemüse und Kräuterquark"),
          ...dish(1090, "Linsen-Curry", "12,90\u00a0€", "vegan, mit Basmatireis"),
          ...menuSection(1210, "Dessert"),
          ...dish(1310, "Apfelstrudel", "5,90\u00a0€", "mit Vanillesauce"),
          text({
            text: "Alle Preise in Euro inkl. MwSt. · Informationen zu Allergenen erhältst du bei unserem Team.",
            style: "caption",
            size: 22,
            color: "text-muted",
            x: 90,
            y: 1430,
            w: 900,
            align: "center",
          }),
        ],
      },
    ],
  },
];

export const PRINT: LibraryTemplate[] = [...FIRST_PRINT, ...MORE_PRINT];
